import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBottomRightStack } from "@/components/BottomRightStack";
import useAnyDialogOpen from "@/hooks/useAnyDialogOpen";
import { useChatHidden, setChatHidden } from "@/hooks/useChatWidgetHidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Minimize2, ShieldCheck, Star, CheckCircle2, Sparkles, ThumbsUp, ThumbsDown, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { analytics } from "@/instrument";

// Category-aware one-tap suggestions shown above the composer when the
// conversation is empty. The canonical list now lives in the backend
// (`services/chat_bot_seed.py::SUGGESTED_QUESTIONS`) — fetched at widget
// mount from `GET /api/chat/suggested-questions` so audit + UI share a
// single source of truth and admins can tweak chip copy without a
// frontend redeploy. This local default is the offline-fallback shape
// (used only if the fetch fails before the patient interacts).
const SUGGESTED_QUESTIONS_FALLBACK = {
  clinical: [
    "What are common side effects?",
    "How do I store my vial?",
    "What if I miss a dose?",
  ],
  orders: [
    "Where's my order?",
    "How do I update my shipping address?",
    "When is my next refill?",
  ],
  billing: [
    "Can I get a receipt?",
    "Do you accept HSA / FSA?",
    "Can I get a refund?",
  ],
  general: [
    "How do I pause my plan?",
    "What states do you serve?",
    "How do I cancel?",
  ],
};

// Render a chat-message body with three small enrichments:
//  • [label](url) markdown link → <a href="url"> (router-aware: same-origin
//    `/foo` paths use react-router so the SPA shell stays mounted; external
//    URLs open in a new tab with rel="noopener")
//  • **bold** markdown → <strong>
//  • US-style phone numbers (e.g. (727) 516-2142, (727) 516-2142, (727) 516-2142,
//    (727) 516-2142, (727) 516-2142) → <a href="tel:...">
// Kept tiny and inline — there is no message-rich-text editor anywhere else
// in the patient widget, so pulling a markdown lib in would be overkill.
function renderMessageText(text, isMe) {
  if (!text) return null;
  const phoneRegex = /(\+?1[-.\s]?)?\(?([2-9]\d{2}|[A-Z]{3})\)?[-.\s]?([A-Z0-9]{3})[-.\s]?([A-Z0-9]{4})/g;
  // Vanity-letter map for tel: links — A=2, B=2, C=2, D=3, ...
  const vanityMap = { A:"2", B:"2", C:"2", D:"3", E:"3", F:"3", G:"4", H:"4", I:"4",
                      J:"5", K:"5", L:"5", M:"6", N:"6", O:"6", P:"7", Q:"7", R:"7",
                      S:"7", T:"8", U:"8", V:"8", W:"9", X:"9", Y:"9", Z:"9" };
  const toDigits = (raw) => "+1" + raw.toUpperCase().replace(/[^A-Z0-9]/g, "")
    .split("").map(c => /[A-Z]/.test(c) ? (vanityMap[c] || "") : c).join("").slice(-10);

  const linkifyPhones = (chunk, keyPrefix) => {
    const out = [];
    let lastIndex = 0;
    let match;
    const re = new RegExp(phoneRegex.source, "g");
    while ((match = re.exec(chunk)) !== null) {
      const before = chunk.slice(lastIndex, match.index);
      if (before) out.push(before);
      const raw = match[0];
      const tel = toDigits(raw);
      out.push(
        <a
          key={`${keyPrefix}-tel-${match.index}`}
          href={`tel:${tel}`}
          className={`underline font-semibold ${isMe ? "text-white" : "text-purple-700 hover:text-purple-900"}`}
          data-testid="chat-tel-link"
        >
          {raw}
        </a>
      );
      lastIndex = match.index + raw.length;
    }
    if (lastIndex < chunk.length) out.push(chunk.slice(lastIndex));
    return out;
  };

  // Walk the text once, splitting on [label](url) link markdown. Anything
  // outside a match goes through the bold/phone pipeline. This ordering
  // matters: links are parsed FIRST so a `**[Log in](/login)**` pattern
  // doesn't accidentally swallow the bracket pair into a bold run.
  const linkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  const segments = [];
  let cursor = 0;
  let m;
  while ((m = linkRegex.exec(text)) !== null) {
    if (m.index > cursor) segments.push({ kind: "text", value: text.slice(cursor, m.index) });
    segments.push({ kind: "link", label: m[1], href: m[2] });
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) segments.push({ kind: "text", value: text.slice(cursor) });

  const renderTextSegment = (chunk, segIdx) => {
    // Inside a text segment, split on **bold** markers (preserving groups)
    // and run phones inside each chunk.
    const boldParts = chunk.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        const inner = part.slice(2, -2);
        return <strong key={`s${segIdx}-b${i}`}>{linkifyPhones(inner, `s${segIdx}-b${i}`)}</strong>;
      }
      return <span key={`s${segIdx}-t${i}`}>{linkifyPhones(part, `s${segIdx}-t${i}`)}</span>;
    });
  };

  return segments.map((seg, i) => {
    if (seg.kind === "link") {
      const isExternal = /^https?:\/\//i.test(seg.href);
      const cls = `underline font-semibold ${isMe ? "text-white" : "text-purple-700 hover:text-purple-900"}`;
      // Same-origin paths use a plain anchor too (full nav is fine — the
      // bot's deep-links are typically /login or /register where a fresh
      // render is the desired UX). Keeps the component free of a router
      // dependency for this single use case.
      return (
        <a
          key={`lnk-${i}`}
          href={seg.href}
          className={cls}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          data-testid="chat-deep-link"
        >
          {seg.label}
        </a>
      );
    }
    return <span key={`seg-${i}`}>{renderTextSegment(seg.value, i)}</span>;
  });
}

export default function PatientChatWidget() {
  const { user } = useAuth();
  // Authenticated patients get persisted conversations + ratings; anonymous
  // visitors get an in-memory bot-only session (no PHI tooling, no SMS
  // escalation). The role check excludes staff/admin tabs from rendering
  // the patient widget even when this component is mounted globally.
  const isAuthedPatient = user && user.role === "patient";
  const isStaffUser = user && user.role && user.role !== "patient";
  const [open, setOpen] = useState(false);
  // Reactive session-scoped hidden flag — Profile "Show chat" toggle
  // flips this back to false without a page refresh.
  const hidden = useChatHidden();
  const hideLauncher = useCallback(() => {
    setOpen(false);
    setChatHidden(true);
    toast.success("Chat hidden. Open Profile → Preferences → Show chat to bring it back.");
  }, []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(null);
  const [unread, setUnread] = useState(0);
  // Patient self-triage category: shown as 4 pickers before composer if no
  // active conversation yet. Values must match VALID_CATEGORIES on the
  // backend (general / orders / clinical / billing).
  const [category, setCategory] = useState(null);
  // QA consent — patient must acknowledge "chat may be recorded for quality
  // assurance" before the triage picker enables. Stored on the conversation
  // doc when it's created so we have a tamper-evident record.
  const [consentAck, setConsentAck] = useState(false);
  // Conversation status driven by the message poll's new envelope shape.
  // When staff closes, this flips to "closed" and we swap the composer for
  // the post-chat rating prompt.
  const [convStatus, setConvStatus] = useState("open");
  // Star rating state shown after closure.
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  // Map of activity_id → "up" | "down" — survives until the patient
  // navigates away or the conversation is reset. Stored client-side so a
  // re-render doesn't lose the "you voted" affordance.
  const [helpfulVotes, setHelpfulVotes] = useState({});
  const [suggestedQuestions, setSuggestedQuestions] = useState(SUGGESTED_QUESTIONS_FALLBACK);
  const messagesEnd = useRef(null);
  const pollRef = useRef(null);

  // Single source of truth: the input + send button are gated through this.
  // Phase-5 (Feb 2026): the bot replaces the human queue, so we no longer
  // gate on staff availability — the bot is always "live" if the admin's
  // master switch is on, and if it's off the auto-reply route escalates
  // every message with the clinic-phone copy.
  const triageDone = !!convId || (consentAck && !!category);
  const isClosed = convStatus === "closed";
  const canSend = triageDone && !isClosed;

  useEffect(() => {
    if (isAuthedPatient) loadConversation();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isAuthedPatient]);

  // Fetch the canonical chip catalog from the backend on first mount.
  // Cheap (one GET, ~12 strings), public endpoint — works for both anon
  // visitors and authed patients. Fallback to the hard-coded list if the
  // request fails so the widget still renders chips offline.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/chat/suggested-questions");
        if (!cancelled && data?.questions) setSuggestedQuestions(data.questions);
      } catch {
        // keep the fallback — no-op
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for global "open-chat" events fired from other components
  // (e.g. severe side-effect escalation auto-opens chat with category=clinical).
  useEffect(() => {
    const onOpenChat = (e) => {
      setOpen(true);
      const cat = e?.detail?.category;
      const prefill = e?.detail?.prefill;
      if (cat) setCategory(cat);
      if (prefill) setInput((cur) => cur || prefill);
    };
    window.addEventListener("glpflo:open-chat", onOpenChat);
    return () => window.removeEventListener("glpflo:open-chat", onOpenChat);
  }, []);

  // Hide the chat launcher/panel whenever another modal dialog is open
  // — otherwise the floating pill covers critical modal content (e.g. the
  // patient how-to guide tutorial).
  const anyDialogOpen = useAnyDialogOpen();

  // Single source of truth for our `bottom`: the orchestrator computes
  // it from everyone else's stack registration (cookie banner, FAB,
  // future CTAs). We register ourselves with `priority: 30`, our
  // measured height, and `visible: true` whenever we're rendered (the
  // staff-role early-return below removes us from the stack entirely).
  const bottomPx = useBottomRightStack({
    id: "patient-chat",
    priority: 30,
    visible: !anyDialogOpen,
    height: open ? 500 : 56,
  });
  const chatStyle = { bottom: `${bottomPx}px` };

  useEffect(() => {
    if (open && convId) {
      loadMessages();
      pollRef.current = setInterval(() => loadMessages(), 5000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, convId]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversation = async () => {
    try {
      const { data } = await API.get("/chat/conversations/mine");
      if (data.id) {
        setConvId(data.id);
        setUnread(data.unread_patient || 0);
        setConsentAck(true); // existing conv → consent was already captured
      }
    } catch (e) { console.error("Chat error:", e); }
  };

  const loadMessages = useCallback(async () => {
    if (!convId) return;
    try {
      const { data } = await API.get(`/chat/messages/${convId}`);
      // New envelope: `{messages, status, rating, ...}`. Fall back to bare
      // array if a stale client/backend ever returns the legacy shape.
      const arr = Array.isArray(data) ? data : (data?.messages ?? []);
      const status = Array.isArray(data) ? "open" : (data?.status || "open");
      const existingRating = Array.isArray(data) ? 0 : (data?.rating || 0);
      setMessages(arr);
      setUnread(0);
      setConvStatus(status);
      if (existingRating > 0) {
        setRatingValue(existingRating);
        setRatingSubmitted(true);
      }
    } catch (e) { console.error("Chat error:", e); }
  }, [convId]);

  const sendText = useCallback(async (text, opts = {}) => {
    const userText = (text || "").trim();
    if (!userText || sending) return;
    if (!consentAck || !category) return; // triage gate
    // Per-chip `send_category` override — some chips visually sit under
    // a triage section (e.g. "Clinical" → general info FAQs) but should
    // send through a different category so the bot's hard-escalate
    // guardrail doesn't fire on a question we actually have an answer for.
    const effectiveCategory = opts.sendCategory || category;
    setSending(true);
    // Optimistically render the patient's message so the chat feels instant.
    const localMsg = {
      id: `local-${Date.now()}`,
      sender_role: "patient",
      message: userText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localMsg]);
    setInput("");
    try {
      // Phase-5 wiring: the patient chat is now bot-first. Every message goes
      // through /chat/auto-reply. If the bot can answer with high confidence
      // and there's no clinical question, it returns a `bot_reply` envelope
      // and we display the template text immediately. Otherwise it returns
      // an `escalated` envelope (with patient_message + SMS already fired
      // server-side) and we display the medical-emergency / clinic-phone
      // copy. There is no human-staff queue any more.
      const { data } = await API.post("/chat/auto-reply", {
        conversation_id: convId,
        message: userText,
        category: effectiveCategory,
        source: opts.source || "composer",
      });
      analytics.capture("chat_message_sent", {
        category: effectiveCategory,
        source: opts.source || "composer",
        matched: data?.kind === "bot_reply" && !data?.unanswered,
      });
      const fromName = data?.from_name || "GLPFlo Support";
      const replyText = data?.kind === "bot_reply"
        ? data.text
        : (data?.patient_message
            || "If this is a medical emergency, call 911 immediately. Otherwise call (727) 516-2142.");
      const replyMsg = {
        id: `reply-${Date.now()}`,
        sender_role: "staff",
        sender_name: fromName,
        message: replyText,
        timestamp: new Date().toISOString(),
        kind: data?.kind,
        // activity_id is only present on bot-reply envelopes — used as the
        // dedupe key for the 👍/👎 helpful vote so re-tapping overwrites
        // rather than inserting a second row.
        activity_id: data?.activity_id || null,
      };
      setMessages((prev) => [...prev, replyMsg]);
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [...prev, {
        id: `fallback-${Date.now()}`,
        sender_role: "staff",
        sender_name: "GLPFlo Support",
        message:
          "⚠️ If this is a medical emergency, call 911 immediately.\n\n"
          + "Otherwise please call our clinic at (727) 516-2142.",
        timestamp: new Date().toISOString(),
        kind: "escalated",
      }]);
    } finally {
      setSending(false);
    }
  }, [sending, consentAck, category, convId]);

  // helpfulVotes: { [activity_id]: "up" | "down" }  (final state)
  // pendingDownvote: activity_id whose 👎-reason textarea is currently open
  // (null = none open). We keep this out of helpfulVotes so the
  // "Thanks!" affordance only flips after the reason is submitted/skipped.
  const [pendingDownvote, setPendingDownvote] = useState(null);
  const [downvoteReason, setDownvoteReason] = useState("");

  const postFeedback = useCallback(async (activityId, vote, reason) => {
    if (!activityId) return;
    try {
      await API.post("/chat/auto-reply/feedback", {
        activity_id: activityId,
        vote,
        ...(reason ? { reason } : {}),
      });
      analytics.capture("chat_feedback_vote", { vote, has_reason: !!reason });
    } catch (err) {
      console.error("Helpful vote failed:", err);
    }
  }, []);

  const submitFeedback = useCallback(async (activityId, vote) => {
    if (!activityId) return;
    if (vote === "down") {
      // Open the reason textarea — don't lock in the vote yet so the
      // patient can still cancel by clicking 👍 mid-flight.
      setPendingDownvote(activityId);
      setDownvoteReason("");
      return;
    }
    // Up-vote — instant optimistic flip + fire-and-forget POST.
    setHelpfulVotes((prev) => ({ ...prev, [activityId]: "up" }));
    postFeedback(activityId, "up");
  }, [postFeedback]);

  const submitDownvoteWithReason = useCallback((activityId, withReason) => {
    if (!activityId) return;
    const reason = withReason ? downvoteReason.trim() : "";
    setHelpfulVotes((prev) => ({ ...prev, [activityId]: "down" }));
    setPendingDownvote(null);
    setDownvoteReason("");
    postFeedback(activityId, "down", reason || undefined);
  }, [downvoteReason, postFeedback]);

  const sendMessage = useCallback((e) => {
    e.preventDefault();
    sendText(input);
  }, [input, sendText]);

  const submitRating = async () => {
    if (ratingValue < 1 || !convId) return;
    setRatingSubmitting(true);
    try {
      await API.post(`/chat/conversations/${convId}/rating`, {
        rating: ratingValue,
        comment: ratingComment.trim(),
      });
      setRatingSubmitted(true);
      toast.success("Thanks for the feedback!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit rating");
    } finally {
      setRatingSubmitting(false);
    }
  };

  // After closure + rating (or skipping), let the patient start a brand-new
  // chat. Wipes local state so the consent gate re-shows and a fresh conv
  // is created on the next message.
  const startNewChat = () => {
    setConvId(null);
    setMessages([]);
    setConvStatus("open");
    setCategory(null);
    setConsentAck(false);
    setRatingValue(0);
    setRatingHover(0);
    setRatingComment("");
    setRatingSubmitted(false);
    setInput("");
  };

  // Staff portals (admin / prescriber / IT / customer_service) never render
  // the patient chat widget — they have their own staff-chat panel and
  // showing a patient bot would be confusing.
  if (isStaffUser) return null;

  // While any Radix dialog is open (help guide, consent modals, etc.),
  // render nothing so the launcher can't cover modal content.
  if (anyDialogOpen) return null;
  // Session-hidden by the patient — nothing to render until page refresh.
  if (hidden) return null;

  if (!open) {
    // Capsule/pill-shaped launcher — two-tone (purple + pink) with a small
    // chat glyph in the middle. Reads instantly as "medication" without the
    // visual busy-ness of a full vial illustration.
    return (
      <button
        onClick={() => { setOpen(true); analytics.capture("chat_open", { source: "launcher" }); }}
        className="fixed right-6 z-50 group h-12 w-20 rounded-full flex items-center justify-center overflow-hidden shadow-lg shadow-purple-600/40 hover:shadow-purple-600/60 transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-300"
        style={{
          ...chatStyle,
          background: "linear-gradient(90deg, #7c3aed 0%, #7c3aed 50%, #ec4899 50%, #ec4899 100%)",
        }}
        data-testid="chat-widget-btn"
        aria-label="Open GLPFlo support chat"
        title="GLPFlo support"
      >
        {/* Glossy pill highlight */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-2 top-1 h-2 rounded-full bg-white/30 blur-[1px]" />
        {/* Center divider — subtle break line so it reads as two-halves */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-1 left-1/2 -translate-x-1/2 w-px bg-white/25" />
        {/* Chat glyph — centered over the divider */}
        <MessageCircle className="relative w-5 h-5 text-white drop-shadow-md" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow ring-2 ring-white">
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden transition-[bottom] duration-300" style={chatStyle} data-testid="chat-widget">
      {/* Header */}
      <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold text-sm truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>GLPFlo Support</span>
          {/* Bot is the chat surface — always-available pill, no human-queue check. */}
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-400/25 text-emerald-50"
            data-testid="chat-status-pill-online"
            title="Patient support is available 24/7"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" /> Online
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={hideLauncher} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid="chat-hide-btn" title="Hide chat for this session">
            <EyeOff className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid="chat-minimize-btn" title="Minimize">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid="chat-close-btn" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {/* QA recording consent — must be acknowledged before the triage
            picker enables. Disclosure also surfaces in the email transcript
            and is stamped on the conversation doc with a UTC timestamp. */}
        {!convId && !consentAck && (
          <div className="bg-white border border-purple-200 rounded-xl p-4" data-testid="chat-consent-card">
            <div className="flex items-start gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Before we start</p>
                <p className="text-xs text-slate-600 leading-relaxed mt-1">
                  This chat may be recorded and reviewed for quality assurance, training, and safety purposes.
                  Please don&rsquo;t share sensitive personal data (e.g. credit card numbers, SSN). For
                  medical emergencies, call <span className="font-semibold">911</span>.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setConsentAck(true)}
              className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm mt-2"
              data-testid="chat-consent-accept"
            >
              I understand — continue
            </Button>
          </div>
        )}

        {/* Persistent topic strip — visible whenever a category is selected
            and the chat isn't closed. Replaces the previous "change" link
            that only showed before the first message. Gives the patient a
            one-tap path back to the main menu (resets category + history)
            or a fresh question within the same topic. */}
        {!isClosed && consentAck && category && (
          <div className="sticky top-0 z-10 -mt-3 -mx-3 mb-2 px-3 py-2 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center gap-2" data-testid="chat-topic-strip">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700">
              {category === "clinical" ? "🩺" : category === "orders" ? "📦" : category === "billing" ? "💳" : "💬"} {category}
            </span>
            <div className="flex-1" />
            {messages.length > 0 && (
              <button
                type="button"
                onClick={() => { setMessages([]); setInput(""); }}
                className="text-[11px] text-slate-600 hover:text-purple-700 font-medium px-1.5 py-0.5 rounded hover:bg-purple-50 transition-colors"
                data-testid="chat-new-question-btn"
                title="Clear this thread and ask a new question in the same topic"
              >
                + New question
              </button>
            )}
            <button
              type="button"
              onClick={() => { setCategory(null); setMessages([]); setInput(""); }}
              className="text-[11px] text-slate-600 hover:text-purple-700 font-medium px-1.5 py-0.5 rounded hover:bg-purple-50 transition-colors"
              data-testid="chat-back-to-menu-btn"
              title="Go back to the main menu to pick a different topic"
            >
              ← Main menu
            </button>
          </div>
        )}

        {/* Triage picker — shown only when consent accepted, no conv yet, no category picked */}
        {!convId && consentAck && !category && (
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chat-triage-picker">
            <p className="text-sm font-semibold text-slate-900 mb-1">What can we help with?</p>
            <p className="text-[11px] text-slate-500 mb-3">Pick a topic so we can route you to the right specialist faster.</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "clinical", label: "Clinical", emoji: "🩺", desc: "Side effects, dosage" },
                { id: "orders", label: "Orders", emoji: "📦", desc: "Shipping, refills, tracking" },
                { id: "billing", label: "Billing", emoji: "💳", desc: "Charges, receipts, HSA" },
                { id: "general", label: "General", emoji: "💬", desc: "Something else" },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className="text-left bg-white hover:bg-purple-50 border border-slate-200 hover:border-purple-300 rounded-lg p-2.5 transition-colors group"
                  data-testid={`chat-triage-${c.id}`}
                >
                  <span className="text-base mr-1">{c.emoji}</span>
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-purple-700">{c.label}</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">{c.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {!convId && consentAck && category && messages.length === 0 && (
          <div className="text-center py-2">
            <p className="text-[11px] text-slate-400">Type your question below or tap a suggestion.</p>
          </div>
        )}
        {/* Removed: "No live agents right now" banner — there's no human queue any more. */}
        {messages.length === 0 && consentAck && category && (
          <div className="text-center py-8">
            <MessageCircle className="w-8 h-8 text-purple-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Type your question below — we&rsquo;ll respond right away.</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender_role === "patient";
          const isBotReply = !isMe && msg.kind === "bot_reply" && msg.activity_id;
          const currentVote = isBotReply ? helpfulVotes[msg.activity_id] : null;
          return (
            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${isMe ? "bg-purple-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                {!isMe && <p className="text-[10px] font-semibold text-purple-600 mb-0.5">{msg.sender_name}</p>}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{renderMessageText(msg.message, isMe)}</p>
                <p className={`text-[10px] mt-1 ${isMe ? "text-purple-200" : "text-slate-400"}`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
                {isBotReply && (
                  <div className="mt-2 pt-2 border-t border-slate-100" data-testid={`chat-helpful-row-${msg.activity_id}`}>
                    {currentVote ? (
                      <span className="text-[11px] text-slate-500 italic" data-testid={`chat-helpful-thanks-${msg.activity_id}`}>
                        {currentVote === "up" ? "Thanks for the feedback!" : "Thanks — we'll improve this answer."}
                      </span>
                    ) : pendingDownvote === msg.activity_id ? (
                      <div className="space-y-1.5" data-testid={`chat-downvote-form-${msg.activity_id}`}>
                        <label className="block text-[11px] text-slate-600 font-medium">
                          What would have been more helpful? <span className="text-slate-400">(optional)</span>
                        </label>
                        <textarea
                          value={downvoteReason}
                          onChange={(e) => setDownvoteReason(e.target.value.slice(0, 280))}
                          placeholder="A short note helps us write better answers…"
                          rows={2}
                          maxLength={280}
                          className="w-full text-[11px] rounded-md border border-slate-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none px-2 py-1.5 resize-none"
                          data-testid={`chat-downvote-textarea-${msg.activity_id}`}
                          autoFocus
                        />
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] text-slate-400">{downvoteReason.length}/280</span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => submitDownvoteWithReason(msg.activity_id, false)}
                              className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100"
                              data-testid={`chat-downvote-skip-${msg.activity_id}`}
                            >
                              Skip
                            </button>
                            <button
                              type="button"
                              onClick={() => submitDownvoteWithReason(msg.activity_id, true)}
                              disabled={!downvoteReason.trim()}
                              className="text-[11px] font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed px-2.5 py-1 rounded"
                              data-testid={`chat-downvote-send-${msg.activity_id}`}
                            >
                              Send
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500">Was this helpful?</span>
                        <button
                          type="button"
                          onClick={() => submitFeedback(msg.activity_id, "up")}
                          className="p-1 rounded-full hover:bg-emerald-50 text-slate-500 hover:text-emerald-600 transition-colors"
                          aria-label="Helpful"
                          data-testid={`chat-helpful-up-${msg.activity_id}`}
                        >
                          <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => submitFeedback(msg.activity_id, "down")}
                          className="p-1 rounded-full hover:bg-rose-50 text-slate-500 hover:text-rose-600 transition-colors"
                          aria-label="Not helpful"
                          data-testid={`chat-helpful-down-${msg.activity_id}`}
                        >
                          <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEnd} />
      </div>

      {/* Closed-state — staff has ended the chat. Patient sees a rating
          prompt + "start new chat" CTA instead of the composer. */}
      {isClosed && (
        <div className="border-t border-slate-100 bg-gradient-to-b from-purple-50 to-white p-4 flex-shrink-0" data-testid="chat-closed-card">
          {!ratingSubmitted ? (
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-1">This chat has ended</p>
              <p className="text-xs text-slate-500 mb-3">How was your experience? Your rating helps us improve.</p>
              <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="chat-rating-stars">
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = n <= (ratingHover || ratingValue);
                  return (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setRatingHover(n)}
                      onMouseLeave={() => setRatingHover(0)}
                      onClick={() => setRatingValue(n)}
                      className="transition-transform hover:scale-110"
                      aria-label={`${n} star${n === 1 ? "" : "s"}`}
                      data-testid={`chat-rating-star-${n}`}
                    >
                      <Star
                        className={`w-7 h-7 ${filled ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
                        strokeWidth={1.5}
                      />
                    </button>
                  );
                })}
              </div>
              {ratingValue > 0 && (
                <>
                  <textarea
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    placeholder={ratingValue <= 2 ? "What could we do better?" : "Anything you'd like to add? (optional)"}
                    maxLength={1000}
                    rows={2}
                    className="w-full text-xs rounded-lg border border-slate-200 px-3 py-2 mb-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                    data-testid="chat-rating-comment"
                  />
                  <Button
                    onClick={submitRating}
                    disabled={ratingSubmitting}
                    className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
                    data-testid="chat-rating-submit"
                  >
                    {ratingSubmitting ? "Submitting…" : "Submit rating"}
                  </Button>
                </>
              )}
              <button
                type="button"
                onClick={startNewChat}
                className="w-full mt-2 text-[11px] text-slate-400 hover:text-slate-700 underline"
                data-testid="chat-rating-skip"
              >
                Skip and start a new chat
              </button>
            </div>
          ) : (
            <div className="text-center py-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-900">Thanks for the feedback!</p>
              <p className="text-xs text-slate-500 mb-3">We&rsquo;ll review it to keep improving.</p>
              <Button
                onClick={startNewChat}
                variant="outline"
                className="rounded-full text-sm border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="chat-rating-new"
              >
                Start a new chat
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Suggested-questions chips — shown above the composer when triage
          is done and the conversation is empty. One-tap sends the chip
          text through the same /chat/auto-reply path, cutting first-message
          time and steering patients toward intents the bot routes with
          high confidence. */}
      {!isClosed && triageDone && messages.length === 0 && (
        <div className="px-3 pt-2 pb-1 bg-white border-t border-slate-100 flex-shrink-0" data-testid="chat-suggested-chips">
          <div className="flex items-center gap-1 mb-1.5">
            <Sparkles className="w-3 h-3 text-purple-500" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Suggested</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(suggestedQuestions[category] || suggestedQuestions.general || []).map((q) => {
              // Chip can be either a plain string OR an object
              //   {text, send_category}. The latter lets a chip visually
              //   sit under one triage section (e.g. "Clinical") while
              //   sending through a different category so the bot's
              //   guardrails don't reject it.
              const text = typeof q === "string" ? q : q.text;
              const sendCategory = (typeof q === "object" && q.send_category) || undefined;
              const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <button
                  key={text}
                  type="button"
                  onClick={() => sendText(text, { source: "suggested-chip", sendCategory })}
                  disabled={sending}
                  className="text-[11px] leading-snug px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-colors disabled:opacity-50"
                  data-testid={`chat-suggested-chip-${slug}`}
                >
                  {text}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Input — hidden when the chat is closed */}
      {!isClosed && (
      <form onSubmit={sendMessage} className="p-3 bg-white border-t border-slate-100 flex-shrink-0">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              !consentAck && !convId
                ? "Acknowledge the disclosure above to start…"
                : !category && !convId
                ? "Pick a topic above to begin…"
                : "Type a message..."
            }
            className="rounded-full text-sm"
            disabled={sending || !canSend}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim() || !canSend}
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white w-10 h-10 p-0 flex-shrink-0"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}
